import { useEffect } from 'react';
import { Preferences } from '@capacitor/preferences';
import { RouterProvider } from 'react-router/dom';
import { selectOptionalUser } from 'features/auth/store/authSelectors';
import { setUserData } from 'features/auth/store/authSlice';
import {
  includeEnvLevelBadgeInFavicon,
  includeEnvLevelInTitle,
} from 'features/env/envLevelMarking';
import { useDocumentSnapshotDispatch } from 'features/firebase/hooks/useDocumentSnapshotDispatch';
import { useReportLoading } from 'features/loading';
import { logger } from 'features/logger/logger';
import { datadogRumAddCustomAction } from 'features/monitoring/customDatadogRumActions';
import { useAppDispatch, useAppSelector } from 'features/store/hooks';
import { selectIsSystemInfoReady } from 'features/system-info/store/systemInfoSelectors';
import { loadSystemInfo } from 'features/system-info/store/systemInfoThunks';
import { setDatadogUser } from 'integrations/datadog/datadogUser';
import { router } from 'pages/router';
import { logUnlessPageLeft } from 'shared/utils/pageLeave';
import { setShowDeveloperFeatures } from './features/dev-tools/store/devToolsSlice';

export const App = () => {
  const dispatch = useAppDispatch();
  const isSystemInfoReady = useAppSelector(selectIsSystemInfoReady);
  const user = useAppSelector(selectOptionalUser);

  useEffect(() => {
    if (user?.uid) {
      setDatadogUser(user.uid);
      datadogRumAddCustomAction({ name: 'userLogin' });
    }
  }, [user?.uid]);

  useEffect(() => {
    dispatch(loadSystemInfo()).catch((error: unknown) => {
      logUnlessPageLeft('Failed to load system info', error);
    });
  }, [dispatch]);

  useEffect(() => {
    if (import.meta.env.PUBLIC_ALLOW_DEVELOPER_FEATURES === 'true') {
      Preferences.get({ key: 'showDeveloperFeatures' })
        .then(result => {
          dispatch(setShowDeveloperFeatures(result.value === 'true'));
        })
        .catch((error: unknown) => {
          logger.warn(
            'Failed to read the developer features preference',
            undefined,
            error as Error
          );
        });
    }
  }, [dispatch]);

  useEffect(() => {
    includeEnvLevelInTitle();
    void includeEnvLevelBadgeInFavicon();
  }, []);

  // subscribe to user document
  useDocumentSnapshotDispatch('userData', setUserData, user?.uid);

  useReportLoading(!isSystemInfoReady);

  return isSystemInfoReady ? <RouterProvider router={router} /> : null;
};
